import { useState, useRef } from "react";
import { Autocomplete } from "@/lib/googleMapsApiShim";
import { getGoogleMapsApiKey } from "@/lib/googleMaps";
import { MapPin, Home, Navigation } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { useI18n } from "@/lib/i18n";

const LIBRARIES: ("places" | "geometry" | "drawing" | "visualization")[] = ["places"];

export type AddressSource = "home" | "custom";

export interface AddressSelection {
  source: AddressSource;
  address?: string;
  lat?: number;
  lng?: number;
  city?: string;
  postal_code?: string;
}

interface CustomAddressSelectorProps {
  label: string;
  value?: AddressSelection | null;
  onChange: (selection: AddressSelection | null) => void;
  homeAddress?: {
    address: string;
    city: string;
    postal_code?: string;
    lat?: number;
    lng?: number;
  } | null;
  disabled?: boolean;
}

import { useGoogleMaps } from "@/contexts/GoogleMapsContext";

export function CustomAddressSelector({
  label,
  value,
  onChange,
  homeAddress,
  disabled = false,
}: CustomAddressSelectorProps) {
  const { t } = useI18n();
  const apiKey = getGoogleMapsApiKey();
  const [customAddress, setCustomAddress] = useState(value?.source === "custom" ? value.address || "" : "");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Use global Maps loader
  const { isLoaded, loadError } = useGoogleMaps();
  const canUsePlacesAutocomplete =
    isLoaded &&
    typeof window !== "undefined" &&
    typeof window.google?.maps?.places?.Autocomplete === "function";

  const handleSourceChange = (source: AddressSource) => {
    if (source === "home" && homeAddress) {
      onChange({
        source: "home",
        address: homeAddress.address,
        city: homeAddress.city,
        postal_code: homeAddress.postal_code,
        lat: homeAddress.lat,
        lng: homeAddress.lng,
      });
    } else if (source === "custom") {
      onChange({
        source: "custom",
        address: customAddress,
      });
    } else {
      onChange(null);
      setCustomAddress("");
    }
  };

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;

    const lat = place.geometry.location?.lat();
    const lng = place.geometry.location?.lng();
    const address = place.formatted_address || place.name || "";
    const city = place.address_components?.find((c) => c.types.includes("locality"))?.long_name || "";
    const postal_code =
      place.address_components?.find((c) => c.types.includes("postal_code"))?.short_name || "";

    onChange({
      source: "custom",
      address,
      city,
      postal_code,
      lat: lat || undefined,
      lng: lng || undefined,
    });

    setCustomAddress(address);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select
        value={value?.source || "none"}
        onValueChange={(val) => handleSourceChange(val as AddressSource)}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder={t("addressSelector.selectSource")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">
            {t("addressSelector.none")}
          </SelectItem>
          {homeAddress && (
            <SelectItem value="home">
              <div className="flex items-center gap-2">
                <Home className="h-4 w-4" />
                {t("addressSelector.homeAddress")}
              </div>
            </SelectItem>
          )}
          <SelectItem value="custom">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {t("addressSelector.customAddress")}
            </div>
          </SelectItem>
        </SelectContent>
      </Select>

      {value?.source === "custom" && apiKey && (
        <div className="mt-2">
          {!isLoaded ? (
            <div className="text-xs text-muted-foreground animate-pulse p-2">{t("routeDetail.loading")}</div>
          ) : !canUsePlacesAutocomplete ? (
            <Input
              placeholder={t("account.homeAddressPlaceholder")}
              value={customAddress}
              onChange={(e) => {
                setCustomAddress(e.target.value);
                onChange({
                  source: "custom",
                  address: e.target.value,
                });
              }}
              disabled={disabled}
            />
          ) : (
            <Autocomplete
              onLoad={(autocomplete) => {
                autocompleteRef.current = autocomplete;
              }}
              onPlaceChanged={handlePlaceSelect}
              options={{
                componentRestrictions: { country: "nl" },
                types: ["address"],
              }}
            >
              <Input
                placeholder={t("account.homeAddressPlaceholder")}
                value={customAddress}
                onChange={(e) => {
                  setCustomAddress(e.target.value);
                  onChange({
                    source: "custom",
                    address: e.target.value,
                  });
                }}
                disabled={disabled}
              />
            </Autocomplete>
          )}
        </div>
      )}

      {value && (
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm">
              <p className="font-medium">
                {value.source === "home" && t("addressSelector.home")}
                {value.source === "custom" && t("addressSelector.custom")}
              </p>
              <p className="text-muted-foreground">
                {value.address ||
                  (value.lat != null && value.lng != null
                    ? `${value.lat?.toFixed?.(4) ?? "0.0000"}, ${value.lng?.toFixed?.(4) ?? "0.0000"}`
                    : t("addressSelector.noCoordinates"))
                }
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
